import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import {
  DataGrid,
  useGridApiContext,
  type GridColDef,
  type GridRenderEditCellParams,
  type GridRowModel,
} from '@mui/x-data-grid'
import {
  TASK_PRIORITIES,
  TASK_PRIORITY_LABELS,
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  type Paginated,
  type TaskDto,
} from '@lawyer/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { ApiError, api, type QueryParams } from '../../lib/api'
import { dueDateHint, formatDate, priorityColor, priorityLabel, statusColor, statusLabel } from '../../lib/format'
import { qk } from '../../lib/queryClient'
import { useUsers } from '../../hooks/useLookups'
import { DateTextField } from '../common/fields'
import { ErrorAlert } from '../common/Feedback'

/** Local midnight, so the grid's date editor and our `YYYY-MM-DD` strings agree. */
const toDateValue = (value: string | null): Date | null =>
  value ? new Date(`${value}T00:00:00`) : null

const toDateString = (value: Date | null | undefined): string | null => {
  if (!value) return null
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${value.getFullYear()}-${month}-${day}`
}

/** DD/MM/YYYY editor that takes typing — the grid's built-in date editor is a native
 *  input that follows the browser locale instead. */
function DueDateEditCell(props: GridRenderEditCellParams<TaskDto, Date | null>) {
  const apiRef = useGridApiContext()
  return (
    <DateTextField
      autoFocus
      variant="standard"
      sx={{ px: 0.5 }}
      value={toDateString(props.value) ?? ''}
      onChange={(next) =>
        void apiRef.current.setEditCellValue({
          id: props.id,
          field: props.field,
          value: toDateValue(next || null),
        })
      }
    />
  )
}

type Props = {
  query: QueryParams
  onOpenTask: (taskId: string) => void
  /** Columns to leave out when the context already implies them. */
  hiddenColumns?: string[]
  height?: number | string
}

export function TasksTable({ query, onOpenTask, hiddenColumns = [], height = 560 }: Props) {
  const queryClient = useQueryClient()
  const users = useUsers()
  const [updateError, setUpdateError] = useState<string | null>(null)

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: qk.tasks(query),
    queryFn: () => api.get<Paginated<TaskDto>>('/tasks', query),
  })

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.patch<TaskDto>(`/tasks/${id}`, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: qk.myDay })
    },
  })

  /** DataGrid hands us the whole row; only the fields that actually changed are sent. */
  const processRowUpdate = useCallback(
    async (newRow: GridRowModel<TaskDto>, oldRow: GridRowModel<TaskDto>) => {
      const patch: Record<string, unknown> = {}
      if (newRow.status !== oldRow.status) patch.status = newRow.status
      if (newRow.priority !== oldRow.priority) patch.priority = newRow.priority
      if (newRow.assignedUserId !== oldRow.assignedUserId) {
        patch.assignedUserId = newRow.assignedUserId ?? ''
      }
      const nextDue = toDateString(newRow.dueDate as unknown as Date | null)
      if (nextDue !== oldRow.dueDate) patch.dueDate = nextDue ?? ''

      if (Object.keys(patch).length === 0) return oldRow
      return update.mutateAsync({ id: newRow.id, patch })
    },
    [update]
  )

  const userOptions = [
    { value: '', label: 'ללא שיוך' },
    ...(users.data ?? []).map((user) => ({ value: user.id, label: user.name })),
  ]

  const columns: GridColDef<TaskDto>[] = [
    {
      field: 'name',
      headerName: 'משימה',
      flex: 2,
      minWidth: 220,
      renderCell: (params) => (
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => onOpenTask(params.row.id)}
          sx={{ textAlign: 'right', font: 'inherit', color: 'inherit' }}
        >
          {params.row.parentTaskId ? '↳ ' : ''}
          {params.row.name}
          {params.row.subtaskCount ? ` (${params.row.subtaskCount})` : ''}
        </Link>
      ),
    },
    {
      field: 'assignedUserId',
      headerName: 'עובד מטפל',
      width: 150,
      editable: true,
      type: 'singleSelect',
      valueOptions: userOptions,
      getOptionValue: (option) => (option as { value: string }).value,
      getOptionLabel: (option) => (option as { label: string }).label,
    },
    {
      field: 'status',
      headerName: 'סטטוס',
      width: 150,
      editable: true,
      type: 'singleSelect',
      valueOptions: TASK_STATUSES.map((status) => ({
        value: status,
        label: TASK_STATUS_LABELS[status],
      })),
      getOptionValue: (option) => (option as { value: string }).value,
      getOptionLabel: (option) => (option as { label: string }).label,
      renderCell: (params) => (
        <Chip
          size="small"
          label={statusLabel(params.row.status)}
          color={statusColor(params.row.status)}
          variant="outlined"
        />
      ),
    },
    {
      field: 'priority',
      headerName: 'דחיפות',
      width: 120,
      editable: true,
      type: 'singleSelect',
      valueOptions: TASK_PRIORITIES.map((priority) => ({
        value: priority,
        label: TASK_PRIORITY_LABELS[priority],
      })),
      getOptionValue: (option) => (option as { value: string }).value,
      getOptionLabel: (option) => (option as { label: string }).label,
      renderCell: (params) => (
        <Chip
          size="small"
          label={priorityLabel(params.row.priority)}
          color={priorityColor(params.row.priority)}
        />
      ),
    },
    {
      field: 'dueDate',
      headerName: 'תאריך יעד',
      // Wide enough for the date plus its "overdue / today / in N days" hint.
      width: 200,
      editable: true,
      type: 'date',
      valueGetter: (value) => toDateValue(value as string | null),
      renderEditCell: (params) => <DueDateEditCell {...params} />,
      renderCell: (params) => {
        if (!params.row.dueDate) return '—'
        const hint = dueDateHint(params.row.dueDate)
        const overdue = hint.startsWith('באיחור')
        return (
          <span style={{ color: overdue ? '#c62828' : undefined }}>
            {formatDate(params.row.dueDate)}
            {hint ? ` · ${hint}` : ''}
          </span>
        )
      },
    },
    { field: 'caseName', headerName: 'תיק', flex: 1, minWidth: 130 },
    { field: 'clientName', headerName: 'לקוח', flex: 1, minWidth: 130 },
    { field: 'projectName', headerName: 'פרויקט', flex: 1, minWidth: 130 },
    { field: 'parentTaskName', headerName: 'משימת אב', flex: 1, minWidth: 130 },
  ]

  return (
    <>
      {error ? <ErrorAlert error={error} onRetry={() => void refetch()} /> : null}

      <Paper variant="outlined" sx={{ height, width: '100%' }}>
        <DataGrid
          rows={data?.items ?? []}
          columns={columns.filter((column) => !hiddenColumns.includes(column.field))}
          loading={isFetching}
          editMode="cell"
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(err) =>
            setUpdateError(err instanceof ApiError ? err.message : 'העדכון נכשל')
          }
          disableRowSelectionOnClick
          rowHeight={46}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
          sx={{ border: 0, '& .MuiDataGrid-cell:focus-within': { outline: 'none' } }}
        />
      </Paper>

      <Snackbar
        open={Boolean(updateError)}
        autoHideDuration={6000}
        onClose={() => setUpdateError(null)}
      >
        <Alert severity="error" onClose={() => setUpdateError(null)}>
          {updateError}
        </Alert>
      </Snackbar>
    </>
  )
}
